import { useNavigate } from "react-router-dom";
import { T, Icon, StatusPill, fmtKES } from "../businessTheme";
import { useState, useEffect } from "react";
import { supabase } from "../../lib/supabase";

function StatCard({ icon, label, value, sub, trend }) {
  return (
    <div style={{ padding: "20px", borderRadius: 16, border: `1px solid ${T.n150}`, background: T.white }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: T.g50, border: `1px solid ${T.g100}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon name={icon} size={16} color={T.g600} strokeWidth={1.8} />
        </div>
        {trend && (
          <span style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 11.5, fontWeight: 600, color: trend > 0 ? T.g600 : T.red }}>
            <Icon name={trend > 0 ? "TrendingUp" : "TrendingDown"} size={12} strokeWidth={2.4} />
            {Math.abs(trend)}%
          </span>
        )}
      </div>
      <div style={{ fontFamily: T.serif, fontSize: 28, fontWeight: 400, color: T.n900, letterSpacing: "-0.02em", lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: T.n700, marginTop: 6 }}>{label}</div>
      {sub && <div style={{ fontSize: 11.5, color: T.n400, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function QuickAction({ icon, label, onClick }) {
  return (
    <button onClick={onClick} style={{
      display: "flex", alignItems: "center", gap: 12, padding: "14px 16px",
      borderRadius: 12, border: `1px solid ${T.n150}`, background: T.white, cursor: "pointer",
      fontFamily: T.sans, textAlign: "left", width: "100%", transition: "border-color .15s, background .15s",
    }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = T.g200; e.currentTarget.style.background = T.g50; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = T.n150; e.currentTarget.style.background = T.white; }}>
      <div style={{ width: 34, height: 34, borderRadius: 10, background: T.n50, border: `1px solid ${T.n150}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon name={icon} size={15} color={T.n600} strokeWidth={1.8} />
      </div>
      <span style={{ fontSize: 13.5, fontWeight: 600, color: T.n800 }}>{label}</span>
      <Icon name="ChevronRight" size={14} color={T.n300} strokeWidth={2} style={{ marginLeft: "auto" }} />
    </button>
  );
}

/* ── Single usage row: label, "used of total", and a progress bar ── */
function UsageRow({ icon, label, used, total, unit, onNavigate }) {
  const unlimited = total === null || total === undefined || total === Infinity;
  const pct = unlimited ? 0 : Math.min(100, Math.round((used / total) * 100));
  const atLimit  = !unlimited && used >= total;
  const nearLimit = !unlimited && !atLimit && pct >= 80;
  const barColor  = atLimit ? T.red : nearLimit ? T.amber : T.g500;

  return (
    <button onClick={onNavigate} style={{
      width: "100%", display: "flex", flexDirection: "column", gap: 9, padding: "16px 18px",
      borderRadius: 14, border: `1px solid ${atLimit ? T.redBorder : T.n150}`,
      background: atLimit ? T.redBg : T.white,
      cursor: onNavigate ? "pointer" : "default",
      fontFamily: T.sans, textAlign: "left",
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <Icon name={icon} size={15} color={T.n500} strokeWidth={1.8} />
          <span style={{ fontSize: 13, fontWeight: 600, color: T.n800 }}>{label}</span>
        </div>
        <span style={{ fontSize: 13, fontWeight: 700, color: atLimit ? T.red : T.n900 }}>
          {used}{unlimited ? "" : ` / ${total}`}{" "}
          <span style={{ fontSize: 11, fontWeight: 500, color: T.n400 }}>{unit}</span>
        </span>
      </div>
      {!unlimited && (
        <div style={{ height: 6, borderRadius: 99, background: T.n100, overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${pct}%`, background: barColor, borderRadius: 99, transition: "width .3s" }} />
        </div>
      )}
      {atLimit && (
        <span style={{ fontSize: 11.5, color: T.red, fontWeight: 600 }}>
          Limit reached — upgrade for more
        </span>
      )}
      {nearLimit && (
        <span style={{ fontSize: 11.5, color: T.amber, fontWeight: 600 }}>
          {total - used} remaining
        </span>
      )}
    </button>
  );
}

export default function OverviewSection({ account, plan, onNavigate }) {
  const navigate = useNavigate();
  const [recentBookings, setRecentBookings] = useState([]);
  const [stats, setStats] = useState({
    activeBookings: 0, reservedPros: 0, monthToDateSpend: 0,
    completionRate: 0, openDisputes: 0,
  });
  const [prosUsed, setProsUsed]   = useState(0);
  const [seatsUsed, setSeatsUsed] = useState(0);
  const [teamSnapshot, setTeamSnapshot] = useState([]);

  useEffect(() => {
    if (!account?.id) return;
    (async () => {
      const startOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString();

      const { data: jobs } = await supabase
        .from("job_requests")
        .select("id, status, payment_status, estimated_price, description, pro_id, created_at, profiles!pro_id(full_name)")
        .eq("business_id", account.id)
        .order("created_at", { ascending: false });

      const { count: reservedCount } = await supabase
        .from("business_reserved_pros")
        .select("id", { count: "exact", head: true })
        .eq("business_id", account.id)
        .eq("status", "active");

      const { count: memberCount } = await supabase
        .from("business_team_members")
        .select("id", { count: "exact", head: true })
        .eq("business_id", account.id)
        .eq("status", "active");

      const { count: openDisputeCount } = await supabase
        .from("reports")
        .select("id", { count: "exact", head: true })
        .in("booking_id", (jobs || []).map(j => j.id))
        .eq("status", "pending");

      const thisMonthJobs = (jobs || []).filter(j => j.created_at >= startOfMonth);
      const completed = thisMonthJobs.filter(j => j.status === "completed");
      const active = (jobs || []).filter(j => ["submitted", "matched", "confirmed"].includes(j.status));
      const spend = thisMonthJobs
        .filter(j => j.payment_status === "paid")
        .reduce((sum, j) => sum + (j.estimated_price || 0), 0);

      // Real avg time-to-accept, from jobs the trigger has actually
      // timestamped — jobs created before the trigger existed simply
      // won't have accepted_at set, and are correctly excluded rather
      // than guessed.
      const { data: acceptedJobs } = await supabase
        .from("job_requests")
        .select("created_at, accepted_at")
        .eq("business_id", account.id)
        .not("accepted_at", "is", null);

      let avgResponseTime = "—";
      if (acceptedJobs && acceptedJobs.length > 0) {
        const totalMs = acceptedJobs.reduce((sum, j) => sum + (new Date(j.accepted_at) - new Date(j.created_at)), 0);
        const avgMinutes = Math.round(totalMs / acceptedJobs.length / 60000);
        avgResponseTime = avgMinutes < 60 ? `${avgMinutes} min` : `${(avgMinutes / 60).toFixed(1)} hr`;
      }

      setStats({
        activeBookings: active.length,
        reservedPros: reservedCount || 0,
        monthToDateSpend: spend,
        completionRate: thisMonthJobs.length ? Math.round((completed.length / thisMonthJobs.length) * 100) : 0,
        openDisputes: openDisputeCount || 0,
        avgResponseTime,
      });
      setProsUsed(reservedCount || 0);
      setSeatsUsed(memberCount || 0);
      setRecentBookings((jobs || []).slice(0, 4).map(j => ({
        id: j.id,
        service: j.description || "Booking",
        pro: j.profiles?.full_name || "Unassigned",
        date: new Date(j.created_at).toLocaleDateString("en-KE", { month: "short", day: "numeric" }),
        status: j.status,
      })));

      // Real team snapshot — replaces MOCK_TEAM_PROS in the "team right
      // now" card below.
      const { data: reservedRows } = await supabase
        .from("business_reserved_pros")
        .select("pro_id, pro_profiles!pro_id ( profiles!user_id ( full_name ) )")
        .eq("business_id", account.id)
        .eq("status", "active");

      const currentlyOnJob = new Set(active.map(j => j.pro_id));
      setTeamSnapshot((reservedRows || []).map(r => {
        const name = r.pro_profiles?.profiles?.full_name || "Pro";
        return {
          id: r.pro_id,
          initials: name.split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase(),
          colorSet: { bg: "#EEF2FF", border: "#C7D2FE", text: "#4338CA" },
          status: currentlyOnJob.has(r.pro_id) ? "on_job" : "available",
        };
      }));
    })();
  }, [account?.id]);

  // ── Usage: ALL values come from props — never fetch here ─────────────────
  const bookingsUsed = account.bookingsUsedThisMonth ?? 0;

  // plan fields — handle both old key names (maxPros/maxTeamMembers) and new
  // PLAN_LIMITS keys (teamSlots/memberSeats) so either mock data or real data works
  const proLimit    = plan.teamSlots     ?? plan.maxPros         ?? null;
  const seatLimit   = plan.memberSeats   ?? plan.maxTeamMembers  ?? null;
  const bookingsCap = plan.bookingsPerMonth ?? null;

  return (
    <div>
      {/* Welcome */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontFamily: T.serif, fontSize: 26, fontWeight: 400, color: T.n900, margin: "0 0 4px", letterSpacing: "-0.02em" }}>
          Welcome back, {account.name?.split(" ")[0] ?? "there"}
        </h2>
        <p style={{ fontSize: 13.5, color: T.n400, margin: 0 }}>
          Here is what is happening across your account today.
        </p>
      </div>

      {/* Subscription usage */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, color: T.n900, margin: 0, letterSpacing: "0.02em" }}>
            {plan.label} plan usage this month
          </h3>
          <button
            onClick={() => onNavigate("billing")}
            style={{ background: "none", border: "none", cursor: "pointer", color: T.g600, fontSize: 12.5, fontWeight: 600, fontFamily: T.sans }}>
            Manage plan
          </button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
          <UsageRow
            icon="Users"
            label="Reserved Pro slots"
            used={prosUsed}
            total={proLimit}
            unit="used"
            onNavigate={() => onNavigate("team")}
          />
          <UsageRow
            icon="Calendar"
            label="Bookings this month"
            used={bookingsUsed}
            total={bookingsCap}
            unit="used"
            onNavigate={() => onNavigate("bookings")}
          />
          <UsageRow
            icon="UserCog"
            label="Team members"
            used={seatsUsed}
            total={seatLimit}
            unit="active"
            onNavigate={() => onNavigate("members")}
          />
        </div>
      </div>

      {/* Stat grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14, marginBottom: 28 }}>
        <StatCard icon="Calendar"    label="Active bookings"    value={stats.activeBookings}          sub="across all locations" />
        <StatCard icon="Users"       label="Reserved Pros"      value={stats.reservedPros}            sub="on your team" />
        <StatCard icon="Wallet"      label="Month-to-date spend" value={fmtKES(stats.monthToDateSpend)} trend={8} />
        <StatCard icon="Clock"       label="Avg. response time"  value={stats.avgResponseTime ?? "—"}  sub="from Pro acceptance" />
        <StatCard icon="CheckCircle" label="Completion rate"     value={`${stats.completionRate}%`}    sub="last 30 days" trend={2} />
        <StatCard icon="Shield"      label="Open disputes"       value={stats.openDisputes}            sub="all clear" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 16 }}>
        {/* Recent bookings */}
        <div style={{ border: `1px solid ${T.n150}`, borderRadius: 16, background: T.white, overflow: "hidden" }}>
          <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.n100}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <h3 style={{ fontSize: 14.5, fontWeight: 700, color: T.n900, margin: 0 }}>Recent bookings</h3>
            <button
              onClick={() => onNavigate("bookings")}
              style={{ background: "none", border: "none", cursor: "pointer", color: T.g600, fontSize: 12.5, fontWeight: 600, fontFamily: T.sans }}>
              View all
            </button>
          </div>
          {recentBookings.map((b, i) => (
            <div key={b.id} style={{
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
              padding: "14px 20px",
              borderBottom: i < recentBookings.length - 1 ? `1px solid ${T.n100}` : "none",
            }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: T.n900, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{b.service}</div>
                <div style={{ fontSize: 12, color: T.n400, marginTop: 2 }}>{b.pro} · {b.date}</div>
              </div>
              <StatusPill status={b.status} />
            </div>
          ))}
        </div>

        {/* Quick actions + team snapshot */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ border: `1px solid ${T.n150}`, borderRadius: 16, background: T.white, padding: "16px" }}>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: T.n900, margin: "0 0 12px", letterSpacing: "0.02em" }}>Quick actions</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <QuickAction icon="Plus"     label="New booking"    onClick={() => onNavigate("bookings")} />
              <QuickAction
                icon="UserPlus"
                label="Reserve a Pro"
                onClick={() => navigate("/search?mode=business-pro&context=reserve")}
              />
              <QuickAction icon="MapPin"  label="Add location"   onClick={() => onNavigate("locations")} />
            </div>
          </div>

          <div style={{ border: `1px solid ${T.g100}`, borderRadius: 16, background: T.g50, padding: "16px" }}>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: T.g700, margin: "0 0 4px" }}>Your team right now</h3>
            <p style={{ fontSize: 12, color: T.g600, margin: "0 0 12px" }}>
              {teamSnapshot.filter(p => p.status === "available").length} available ·{" "}
              {teamSnapshot.filter(p => p.status === "on_job").length} on a job
            </p>
            <div style={{ display: "flex" }}>
              {teamSnapshot.slice(0, 4).map((p, i) => (
                <div key={p.id} style={{
                  width: 32, height: 32, borderRadius: "50%",
                  background: p.colorSet.bg, border: `2px solid ${T.white}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 11, fontWeight: 700, color: p.colorSet.text,
                  marginLeft: i > 0 ? -8 : 0,
                }}>
                  {p.initials}
                </div>
              ))}
            </div>
            <button
              onClick={() => onNavigate("team")}
              style={{ marginTop: 12, background: "none", border: "none", cursor: "pointer", color: T.g700, fontSize: 12.5, fontWeight: 600, fontFamily: T.sans, display: "flex", alignItems: "center", gap: 5, padding: 0 }}>
              Manage team <Icon name="ChevronRight" size={12} strokeWidth={2.4} color={T.g700} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}